import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { baseGetRequest, basePostRequest } from "./baseRequests";
import test from "node:test";

const getFetcher = (url:string) => baseGetRequest({url});
const postFetcher = (
    url: string,
    { arg }: { arg: Record<string, any> }
) => basePostRequest({ url, body: arg });

//////////////////////////////////
// GET
//////////////////////////////////

export const useGetUserQuery = (pk:number | null | undefined = undefined) => {
    const extension = pk ? `/${pk.toString()}/` : "";
    const { data, error, isLoading, mutate } = useSWR(
        `/api/user${extension}`,
        getFetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 30000,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

export const useGetExamBaseQuery = (pk:number | null | undefined = undefined) => {
    const extension = pk ? `/${pk.toString()}/` : ""; 
        const { data, error, isLoading, mutate } = useSWR(
        `/api/exam-base${extension}`,
        getFetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 30000,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

export const useGetExamComponentBaseQuery = (pk:string |null | undefined = undefined, exam_base_id:string | null | undefined = undefined) => {
    let extension = "";
    if (pk) {
        extension = `/${pk}`;
    } else if (exam_base_id) {
        extension = `/?exam_base_id=${exam_base_id}`;
    }
    const { data, error, isLoading, mutate } = useSWR(
        `/api/exam-component-base${extension}`,
        getFetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 30000,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

export const useLazyGetExamComponentBaseQuery = (
    pk:string |null | undefined = undefined, exam_base_id:string | null | undefined = undefined
) => {
    let extension = "";
    if (pk) {
        extension = `/${pk}`;
    } else if (exam_base_id) {
        extension = `/?exam_base_id=${exam_base_id}`;
    }

    const key = `/api/exam-component-base${extension}`;

    const { trigger, data, error, isMutating } = useSWRMutation(
        key,
        (url: string) => getFetcher(url)
    );
    return {
        run: trigger,
        data,
        loading: isMutating,
        error,
    };
};


export const useGetExamComponentQuery = (pk:string |null | undefined = undefined, exam_component_base_id:string | null | undefined = undefined, all_max:boolean = false, test_taker_id:number | null | undefined =undefined) => {
    let extension = "";
    if (pk) {
        extension = `/${pk}`;
    } else if (exam_component_base_id) {
        extension = `/?exam_component_base_id=${exam_component_base_id}`;
    } else if (test_taker_id && all_max) {
        extension = `/all_max=true&test_taker_id=${test_taker_id.toString()}`
    } else if (all_max) {
        extension = `/?all_max=true`;
    }
    const { data, error, isLoading, mutate } = useSWR(
        `/api/exam-component${extension}`,
        getFetcher,
        {
            revalidateOnFocus: true,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

export const useLazyGetExamComponentQuery = (
    pk?: string | null,
    exam_component_base_id?: string | null,
    all_max: boolean = false,
    test_taker_id?: number | null
) => {
    let extension = "";

    if (pk) {
        extension = `/${pk}`;
    } else if (exam_component_base_id) {
        extension = `/?exam_component_base_id=${exam_component_base_id}`;
    } else if (test_taker_id && all_max) {
        extension = `/?all_max=true&test_taker_id=${test_taker_id}`;
    } else if (test_taker_id) {
        extension = `/?test_taker_id=${test_taker_id}`;
    } else if (all_max) {
        extension = `/?all_max=true`;
    }

    const key =
        !all_max && !test_taker_id
            ? null
            : `/api/exam-component${extension}`;

    const { trigger, data, error, isMutating } = useSWRMutation(
        key,
        (url: string) => getFetcher(url)
    );

    return {
        run: trigger,
        data,
        loading: isMutating,
        error,
    };
};

export const useGetQuestionQuery = (pk:string |null | undefined = undefined, exam_component_id:string | null | undefined = undefined, question_number:string | null | undefined = undefined) => {
    let extension = "";
    if (pk) {
        extension = `/${pk}`;
    } else if (exam_component_id && question_number) {
        extension = `/?exam_component_id=${exam_component_id}&question_number=${question_number}`;
    }
    const { data, error, isLoading, mutate } = useSWR(
        `/api/question${extension}`,
        getFetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 30000,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

export const useGetAnswerChoiceQuery = (pk:string |null | undefined = undefined, exam_component_id:string | null | undefined = undefined, question_number:string | null | undefined = undefined) => {
    let extension = "";
    if (pk) {
        extension = `/${pk}`;
    } else if (exam_component_id && question_number) {
        extension = `/?exam_component_id=${exam_component_id}&question_number=${question_number}`;
    }
    const { data, error, isLoading, mutate } = useSWR(
        `/api/answer-choice${extension}`,
        getFetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 30000,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

export const useGetUserAnswerQuery = (pk:string |null | undefined = undefined, exam_component_id:string | null | undefined = undefined, question_number:string | null | undefined = undefined) => {
    let extension = "";
    if (pk) {
        extension = `/${pk}`;
    } else if (exam_component_id && question_number) {
        extension = `/?exam_component_id=${exam_component_id}&question_number=${question_number}`;
    } else if (exam_component_id) {
        extension = `/?exam_component_id=${exam_component_id}`;
    }
    const { data, error, isLoading, mutate } = useSWR(
        `/api/user-answer${extension}`,
        getFetcher,
        {
            revalidateOnFocus: false,
            revalidateOnMount: true,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

export const useGetReservationQuestionQuery = (pk:string |null | undefined = undefined) => {
    let extension = "";
    if (pk) {
        extension = `/${pk}`;
    } 
    const { data, error, isLoading, mutate } = useSWR(
        `/api/reservation-question${extension}`,
        getFetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 30000,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

export const useGetTimeSlotQuery = (pk:string|null|undefined) => {
    let extension = "";
    if (pk) {
        extension = `/${pk}`;
    } 
    const { data, error, isLoading, mutate } = useSWR(
        `/api/timeslot${extension}`,
        getFetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 30000,
        }
    );
    return  { data, loading: isLoading, error, refresh: mutate }
};

//////////////////////////////////
// POST
//////////////////////////////////
export const usePostExamComponentMutation = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        `/api/exam-component/`,
        postFetcher,
    );
    return { submit: trigger, data, error, loading: isMutating };
};

export const usePostUserAnswerMutation = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        `/api/user-answer/`,
        postFetcher
    );
    return { submit: trigger, data, error, loading: isMutating };
};

export const usePostUserMutation = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        `/api/user/`,
        postFetcher
    );
    return { submit: trigger, data, error, loading: isMutating };
};

export const usePostScheduledTesterMutation = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        `/api/scheduled-tester/`,
        postFetcher
    );
    return { submit: trigger, data, error, loading: isMutating };
};

export const usePostExamConfirmationEmailMutation = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        `/api/send-exam-confirmation-email/`,
        postFetcher
    );
    return { submit: trigger, data, error, loading: isMutating };
};

export const usePostQuestionMutation = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        `/api/question/`,
        postFetcher
    );
    return { submit: trigger, data, error, loading: isMutating };
};

export const usePostAnswerChoiceMutation = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        `/api/answer-choice/`,
        postFetcher
    );
    return { submit: trigger, data, error, loading: isMutating };
};